import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getIsHR, getIsLeader, getIsManagement, getIsProjectLeader } from '../../store/redux/reducers/currentUserSlice'
import { StyledContainerColumnLeft } from '../../styles/ContainerStyles'
import { Assignment, ContentPaste, People, Person } from '@mui/icons-material'
import ReportTile from './ReportTile'
import { reportTiles, ReportTileValue } from '../../store/constants/reportConstants'

type ReportsTypeTilesProps = {
  selectedTile: ReportTileValue | null
  onSelect: (tileName: ReportTileValue) => void
}

const ReportsTypeTiles = ({ selectedTile, onSelect }: ReportsTypeTilesProps) => {
  const { t } = useTranslation()

  const theme = useTheme()
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'))
  const isDownXs = useMediaQuery(theme.breakpoints.down(380))

  const isManagement = useSelector(getIsManagement)
  const isLeader = useSelector(getIsLeader)
  const isProjectLeader = useSelector(getIsProjectLeader)
  const isHR = useSelector(getIsHR)

  useEffect(() => {
    if (!isManagement && !isLeader && !isProjectLeader) {
      onSelect(reportTiles.CONTRACT)
    }
  }, [isManagement, isLeader, isProjectLeader, onSelect])

  return (
    <StyledContainerColumnLeft>
      <Typography variant="h2" sx={{ color: 'text.highEmphasis' }}>
        {t('page.reports.reportsType')}
      </Typography>
      <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.mediumEmphasis' }}>
        {t('page.reports.reportsTypeCaption')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isDownLg ? 'row' : 'column',
          gap: !isDownXs ? 0.5 : 0.2,
          justifyContent: 'space-evenly',
          flexWrap: isDownLg ? 'wrap' : 'none',
          mt: '31px',
        }}
      >
        {(isManagement || isProjectLeader) && (
          <ReportTile
            icon={<ContentPaste />}
            title={t('page.reports.projects')}
            description={t('page.reports.projectsTileCaption')}
            selected={selectedTile === reportTiles.PROJECTS}
            onSelect={() => onSelect(reportTiles.PROJECTS)}
          />
        )}

        {(isLeader || isManagement) && (
          <>
            <ReportTile
              icon={<People />}
              title={t('page.reports.users')}
              description={t('page.reports.usersTileCaption')}
              selected={selectedTile === reportTiles.USERS}
              onSelect={() => onSelect(reportTiles.USERS)}
            />
            <ReportTile
              icon={<Person />}
              title={t('page.reports.concreteUser')}
              description={t('page.reports.concreteUserCaption')}
              selected={selectedTile === reportTiles.CONCRETE_USER}
              onSelect={() => onSelect(reportTiles.CONCRETE_USER)}
            />
          </>
        )}

        {(isHR || isManagement) && (
          <ReportTile
            icon={<Assignment />}
            title={t('page.reports.contract')}
            description={t('page.reports.contractTileCaption')}
            selected={selectedTile === reportTiles.CONTRACT}
            onSelect={() => onSelect(reportTiles.CONTRACT)}
          />
        )}

        {isManagement && (
          <ReportTile
            icon={<ContentPaste />}
            title={t('page.reports.technologies')}
            description={t('page.reports.technologyReportCaption')}
            selected={selectedTile === reportTiles.TECHNOLOGY}
            onSelect={() => onSelect(reportTiles.TECHNOLOGY)}
          />
        )}
      </Box>
    </StyledContainerColumnLeft>
  )
}

export default ReportsTypeTiles
